import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import Modal, { Field, TextInput, TextArea, Select, PrimaryButton, SecondaryButton } from "@/components/FormKit";
import StatusBadge from "@/components/StatusBadge";
import { toast } from "sonner";
import { fmtDateTime } from "@/lib/format";
import { Megaphone } from "lucide-react";

const initial = { title: "", body: "", alert_type: "announcement", target_project_id: "" };

export default function Alerts() {
  const [items, setItems] = useState([]);
  const [projects, setProjects] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(initial);

  const load = async () => {
    const [a, p] = await Promise.all([api.get("/company/alerts"), api.get("/company/projects")]);
    setItems(a.data);
    setProjects(p.data);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/company/alerts", {
        ...form,
        target_project_id: form.target_project_id || null,
      });
      toast.success("Alert broadcast to customers");
      setOpen(false);
      setForm(initial);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="space-y-8" data-testid="company-alerts-page">
      <PageHeader
        eyebrow="Broadcast"
        title="Alerts & announcements"
        description="Announce launches, offers, or messages to customers."
        actions={<PrimaryButton onClick={() => setOpen(true)} testId="new-alert-button">+ New Alert</PrimaryButton>}
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {items.map((a) => (
          <div key={a.id} className="grid-surface p-5 border-l-4 border-l-brand-500" data-testid={`alert-${a.id}`}>
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-2">
                <Megaphone size={16} className="text-brand-500" />
                <StatusBadge value={a.alert_type} label={a.alert_type.toUpperCase()} />
              </div>
              <div className="text-xs text-stone-500 font-mono">{fmtDateTime(a.created_at)}</div>
            </div>
            <h3 className="font-display text-lg font-bold mt-3">{a.title}</h3>
            <p className="text-sm text-stone-700 mt-1.5">{a.body}</p>
          </div>
        ))}
        {items.length === 0 && (
          <div className="grid-surface border-dashed p-10 text-center overline text-stone-400 col-span-full">
            No alerts yet
          </div>
        )}
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title="Broadcast alert">
        <form onSubmit={submit}>
          <Field label="Title"><TextInput testId="form-alert-title" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></Field>
          <Field label="Body"><TextArea testId="form-alert-body" rows={4} required value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Type">
              <Select testId="form-alert-type" value={form.alert_type} onChange={(e) => setForm({ ...form, alert_type: e.target.value })}>
                <option value="announcement">Announcement</option>
                <option value="launch">New Launch</option>
                <option value="offer">Offer</option>
              </Select>
            </Field>
            <Field label="Target project (optional)">
              <Select testId="form-alert-target" value={form.target_project_id} onChange={(e) => setForm({ ...form, target_project_id: e.target.value })}>
                <option value="">All customers</option>
                {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </Select>
            </Field>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <SecondaryButton type="button" onClick={() => setOpen(false)}>Cancel</SecondaryButton>
            <PrimaryButton type="submit" testId="submit-alert">Broadcast</PrimaryButton>
          </div>
        </form>
      </Modal>
    </div>
  );
}
